<script setup>

// Props
const props = defineProps({
  block: {
    type: Object,
    default: () => ({})
  },
  pbIndex: {
    type: Number,
    required: false
  }
})

const pbIndex = computed(() => props.pbIndex ?? null)

</script>

<!-- -------------------------------------------------------------------------------------------------
    Template
------------------------------------------------------------------------------------------------- -->

<template>
  <section class="feature-pages-section section-mb" :id="props.block?.anchorId">
    <div class="container">
      <div class="feature-pages__head">
        <div v-if="block?.['headingAdvanced']?.html?.length"
          v-html="block?.['headingAdvanced']?.html"
          class="feature-pages__heading-title"></div>
        
        <p v-if="block?.['description']?.length" class="card-grid__description">
          {{ block?.['description'] }}
        </p>
      </div>

      <div class="feature-pages">

        <component
          v-for="card in block?.featurePagesContent?.[0]?.featureCards"
          :is="card?.linkTitle?.url && !card?.linkTitle?.title ? 'a' : 'div'"
          :key="card.id"
          class="feature-card"
          v-bind="card?.linkTitle?.url && !card?.linkTitle?.title
            ? {
                href: card.linkTitle.url,
                'aria-label': card?.heading || undefined
              }
            : {}"
        >

          <div class="feature-card__image">

            <GlobalsImage
              :portrait="(card?.imageMobile || [])[0]"
              :landscape="(card?.imageDesktop || [])[0]"
              :ariaHidden="Boolean(card?.heading)"
              :loading="pbIndex !== null && pbIndex < 2 ? 'eager' : 'lazy'"
            />

          </div>

          <p v-if="card?.heading"
            class="feature-card__heading">
            {{ card.heading }}
          </p>

          <p v-if="card?.description"
            class="feature-card__desc">
            {{ card.description }}
          </p>

          <GlobalsButton v-if="card?.linkTitle?.url && card?.linkTitle?.title"
            :url="card?.linkTitle?.url"
            :title="card?.linkTitle?.title"
            :ariaLabel="card?.heading
              ? `${card.linkTitle.title} ${card.heading}`
              : card.linkTitle.title"
            type="secondary"
            size="large"
            class="feature-card__btn">
          </GlobalsButton>

        </component>
      </div>
      
    </div>
  </section>
</template>

<!-- -------------------------------------------------------------------------------------------------
    Styling
------------------------------------------------------------------------------------------------- -->

<style scoped>

.feature-pages__head {
  margin-bottom: 32px;
  display: flex;
  flex-direction: column;

  @media (min-width: 1024px) {
    align-items: center;
    text-align: center;
  }

  @media (min-width: 1440px) {
    margin-bottom: 48px;
  }
}

.feature-pages__heading-title :deep(*) {
  font-size: 28px;
  letter-spacing: -0.56px;
  color: var(--color-gray-800);

  @media (min-width: 1440px) {
    font-size: 38px;
    line-height: 120%; /* 45.6px */
    letter-spacing: -0.76px;
  }
}

.card-grid__description {
  color: var(--color-gray-400);
  letter-spacing: -0.32px;
  line-height: 140%;
  margin-top: 16px;

  @media (min-width: 1024px) {
    margin-top: 24px;
    font-size: 18px;
    line-height: 138%; /* 24.84px */
    letter-spacing: -0.36px;
    color: var(--color-gray-500);
  }
}

.feature-pages {
  display: flex;
  flex-direction: column;
}

.feature-card + .feature-card {
  margin-top: 32px;
}

.feature-card {
  display: flex;
  flex-direction: column;
}

.feature-card__image {
  border-radius: 12px;
  overflow: hidden;
  width: 100%;
}

.feature-card__image :deep(img) {
  width: 100%;
}

a.feature-card:hover .feature-card__image :deep(img) {
  transform: scale(1.05);
}

.feature-card__heading {
  font-family: var(--font-family-primary);
  font-size: 20px;
  font-weight: 500;
  line-height: 144%; /* 28.8px */
  letter-spacing: -0.4px;
  color: var(--color-gray-700);
  margin-top: 16px;

  @media (min-width: 1440px) {
    font-size: 28px;
    line-height: 132%; /* 36.96px */
    letter-spacing: -0.56px;
    margin-top: 24px;
  }
}

a.feature-card .feature-card__heading {
  overflow: hidden;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
}

.feature-card__desc {
  font-size: 14px;
  line-height: 140%; /* 19.6px */
  letter-spacing: -0.28px;
  color: var(--color-gray-400);
  margin-top: 4px;

  @media (min-width: 1440px) {
    margin-top: 8px;
  }
}

.feature-card__btn {
  margin-top: 20px;

  @media (min-width: 768px) {
    align-self: flex-start;
    min-width: 112px;
  }
}

@media (min-width: 1024px) {

  .feature-pages-section {
    margin-bottom: 140px;
  }

  .feature-pages__head {
    margin-bottom: 48px;
  }

  .feature-pages__heading-title {
    max-width: 768px;
  }

  .feature-pages__heading-title :deep(*) {
    font-size: 38px;
    line-height: 120%; /* 45.6px */
    letter-spacing: -0.76px;
  }

  .card-grid__description {
    max-width: 964px;
    margin-inline: auto;
  }

  .feature-pages {
    flex-direction: row;
  }
  
  .feature-card {
    width: calc(33.333% - 16px / 3 * 2);
    align-items: flex-start;
  }
  
  .feature-card + .feature-card {
    margin-top: 0;
    margin-left: 16px;
  }

  .feature-card__image {
    border-radius: 16px;
  }

  .feature-card__heading {
    font-size: 28px;
    line-height: 132%; /* 36.96px */
    letter-spacing: -0.56px;
    margin-top: 20px;
  }

  .feature-card__desc {
    flex-grow: 1;
    font-size: 16px;
    line-height: 140%; /* 22.4px */
    letter-spacing: -0.32px;
  }

  .feature-card__btn {
    min-width: 137px;
    margin-top: 20px;
  }

}

</style>


